import React from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as UserEditIcon } from './icons/user-edit.svg';
import { ReactComponent as TrashIcon } from './icons/trash.svg';
import { useLanguage } from './LanguageContext';
import { fetchMembers, deleteMember } from './api';
import Layout from './Layout';

// ── helpers ────────────────────────────────────────────────────────────────
const formatDateTime = (isoString) => {
  if (!isoString) return '—';
  const d = new Date(isoString);
  return d.toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ── Attendance Dropdown Panel ───────────────────────────────────────────────
const AttendancePanel = ({ member }) => {
  const history = member.attendanceHistory || [];

  return (
    <div className="mx-4 mb-2 rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white animate-fadeIn">
      {/* ── Summary bar ── */}
      <div className="flex items-center gap-4 px-6 py-4 bg-gradient-to-r from-[#001621] to-[#002d45]">
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" fill="none"
            viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </span>
        <div>
          <p className="text-[10px] text-white/50 font-poppins uppercase tracking-wider leading-none mb-0.5">Attendance History</p>
          <p className="text-sm text-white font-semibold font-poppins">{history.length} visit{history.length !== 1 ? 's' : ''} recorded</p>
        </div>
      </div>

      {/* ── Attendance table ── */}
      {history.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-slate-400">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 mb-2 opacity-30" fill="none"
            viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-sm font-poppins">No attendance records</p>
        </div>
      ) : (
        <div className="max-h-56 overflow-y-auto">
          <table className="w-full text-sm font-poppins">
            <thead className="sticky top-0 bg-slate-50 z-10">
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date &amp; Time</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reader</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Exit</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.map((att, idx) => (
                <tr
                  key={att.attendance_id || idx}
                  className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}
                >
                  <td className="py-3 px-6 text-slate-700 font-medium whitespace-nowrap">
                    {formatDateTime(att.enter_time)}
                  </td>
                  <td className="py-3 px-4 text-slate-600">{att.reader_name || '—'}</td>
                  <td className="py-3 px-4 text-slate-600">{att.reader_location || '—'}</td>
                  <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                    {att.exit_time ? formatDateTime(att.exit_time) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold
                      ${att.attendance
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-100 text-slate-500'
                      }`}>
                      {att.attendance ? 'Present' : 'Absent'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Main Home Component ─────────────────────────────────────────────────────
const Home = () => {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [members, setMembers] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [expandedId, setExpandedId] = React.useState(null);

  React.useEffect(() => {
    const loadMembers = async () => {
      try {
        const data = await fetchMembers();
        console.log("Full API Response:", data);

        if (Array.isArray(data)) {
          const mappedMembers = data.map(user => ({
            id: user.user_id,
            name: user.name,
            memberId: user.member_id,
            subscriptionType: user.membership_type
              ? user.membership_type.charAt(0).toUpperCase() + user.membership_type.slice(1).toLowerCase()
              : 'Silver',
            status: user.status
              ? user.status.charAt(0).toUpperCase() + user.status.slice(1).toLowerCase()
              : 'Attivo',
            email: user.email,
            phone: user.phone,
            startDate: user.start_date,
            endDate: user.end_date,
            attendanceHistory: user.attendance_history || [],
          }));
          setMembers(mappedMembers);
        } else {
          console.error("API response is not an array:", data);
        }
      } catch (error) {
        console.error("Failed to load members", error);
      }
    };

    loadMembers();
  }, []);

  const handleDelete = async (id) => {
    try {
      await deleteMember(id);
      setMembers(members.filter(member => member.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch (error) {
      console.error("Error deleting member:", error);
    }
  };

  const handleEdit = (member) => {
    navigate('/create', { state: { editMode: true, memberData: member } });
  };

  const toggleExpand = (id) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const filteredMembers = members.filter(member =>
    member.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const subscriptionKeyMap = {
    'Bronze':   'subscription_bronze',
    'Silver':   'subscription_silver',
    'Gold':     'subscription_gold',
    'Platinum': 'subscription_platinum',
  };

  const statusKeyMap = {
    'Attivo':   'status_attivo',
    'Inattivo': 'status_inattivo',
    'Scaduto':  'status_scaduto',
  };

  return (
    <Layout mainClassName="p-10 pt-24">
      {/* Language selector */}
      <div className="absolute right-6 top-6">
        <div className="w-auto bg-gradient-to-b from-white to-[#F0F2F5] rounded-full shadow-[0_8px_16px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_10px_20px_-4px_rgba(0,0,0,0.15)] transition-all">
          <select
            aria-label={t('languageLabel')}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="text-sm bg-transparent px-4 py-2 rounded-full outline-none"
          >
            <option value="it">Italian</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>

      <h1 className="text-3xl font-extrabold text-slate-800 mb-6 pl-7">{t('membersListTitle')}</h1>

      {/* Search Bar */}
      <div className="mb-6 px-7">
        <div className="relative flex items-center max-w-md">
          <span className="absolute left-4 text-[#64748B] pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
              viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
          </span>
          <input
            id="member-search"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('searchPlaceholder')}
            aria-label={t('searchPlaceholder')}
            className="w-full pl-11 pr-10 py-3 rounded-full bg-white border border-slate-200 shadow-sm text-sm text-slate-700 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#001621]/30 focus:border-[#001621]/50 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              className="absolute right-4 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none"
                viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Table Header */}
      <div className="grid grid-cols-12 bg-[#77818A] text-black font-poppins font-semibold text-sm py-6 px-6 rounded-2xl tracking-tight mx-0">
        <div className="col-span-3">{t('table_member')}</div>
        <div className="col-span-2">{t('table_id')}</div>
        <div className="col-span-2">{t('table_subscription')}</div>
        <div className="col-span-2">{t('table_status')}</div>
        <div className="col-span-1 text-center">Attendance</div>
        <div className="col-span-2"></div>
      </div>

      {/* Member List */}
      <div>
        {filteredMembers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-3 opacity-40" fill="none"
              viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <p className="text-sm font-poppins">{t('searchNoResults')}</p>
          </div>
        ) : filteredMembers.map((member) => (
          <div key={member.id}>
            {/* ── Member row ── */}
            <div
              className={`grid grid-cols-12 gap-x-4 items-center py-4 px-4 text-sm text-black border-b border-black transition-colors ${expandedId === member.id ? 'bg-slate-50' : ''}`}
            >
              {/* Name */}
              <div className="flex items-center gap-4 col-span-3">
                <span className="text-black font-medium">{member.name}</span>
              </div>

              {/* Member ID */}
              <div className="col-span-2 text-black font-medium">{member.memberId}</div>

              {/* Subscription */}
              <div className="col-span-2 text-black font-medium">
                {t(subscriptionKeyMap[member.subscriptionType] || member.subscriptionType)}
              </div>

              {/* Status */}
              <div className="col-span-2 text-black font-medium">
                {t(statusKeyMap[member.status] || member.status)}
              </div>

              {/* Attendance toggle button */}
              <div className="col-span-1 flex justify-center">
                <button
                  id={`attendance-toggle-${member.id}`}
                  onClick={() => toggleExpand(member.id)}
                  aria-label={`${expandedId === member.id ? 'Hide' : 'Show'} attendance for ${member.name}`}
                  title={`${member.attendanceHistory.length} visit${member.attendanceHistory.length !== 1 ? 's' : ''}`}
                  className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold font-poppins transition-all shadow-sm
                    ${expandedId === member.id
                      ? 'bg-[#001621] text-white shadow-[#001621]/20 shadow-md'
                      : 'bg-slate-100 text-slate-600 hover:bg-[#001621] hover:text-white'
                    }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{member.attendanceHistory.length}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`w-3 h-3 transition-transform duration-200 ${expandedId === member.id ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 col-span-2">
                <button
                  onClick={() => handleEdit(member)}
                  aria-label={`Edit ${member.name}`}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800 text-white hover:bg-slate-700 transition-colors"
                >
                  <UserEditIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDelete(member.id)}
                  aria-label={`Delete ${member.name}`}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* ── Attendance dropdown panel ── */}
            {expandedId === member.id && (
              <AttendancePanel member={member} />
            )}
          </div>
        ))}
      </div>

      {/* fade-in keyframe (inline style tag since Tailwind JIT may not have it) */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .animate-fadeIn { animation: fadeIn 0.18s ease-out both; }
      `}</style>
    </Layout>
  );
};

export default Home;
